import { projectRequestV2, requestStatusCodes, } from "./action-center.js";
import { noticeIdFromEvent, systemNoticeMessageId } from "./messaging-identity.js";
import { requestEventDedupeKey } from "./request-notification.js";
import { isUnassignedRequest, requestAttentionFlags, requestException, } from "./request-queue.js";
import { requestCustomerStatusText } from "./request-status-text.js";
const STREAM_PLAN_OF_EVENT = Object.freeze({
    CREATED: "NONE",
    DRAFT_UPDATED: "NONE",
    // The first SUBMITTED posts the card. It is the only message that names the request as a whole.
    SUBMITTED: "CARD",
    ASSIGNED: "NOTICE",
    CLAIMED: "NONE",
    AWAITING_CUSTOMER: "NOTICE",
    CUSTOMER_RESPONDED: "NOTICE",
    // A staff member recorded the answer for the customer: the state returns to IN_PROGRESS and the Action Center follows. No outbox row.
    RESPONSE_RECORDED: "NONE",
    PAYMENT_REQUIRED: "NOTICE",
    PAYMENT_RESOLVED: "NONE",
    PAYMENT_REPLANNED: "NONE",
    CONFIRMED: "NOTICE",
    COMPLETED: "NOTICE",
    CANCELLED: "NOTICE",
    UNAVAILABLE: "NOTICE",
    DECLINED: "NOTICE",
    EXPIRED: "NOTICE",
    // Staff-internal. They never reach the customer's chat.
    OWNER_REASSIGNED: "NONE",
    PRIORITY_CHANGED: "NONE",
    STATUS_SUMMARY_UPDATED: "NONE",
    WORK_STATUS_CHANGED: "NONE",
});
export function requestStreamPlanOfEvent(event) {
    return STREAM_PLAN_OF_EVENT[event];
}
/** The events that make a customer notice, in no order: the stale rule of `isRequestNoticeStale` compares their versions. */
export const requestNoticeEvents = Object.freeze(Object.keys(STREAM_PLAN_OF_EVENT).filter((event) => STREAM_PLAN_OF_EVENT[event] === "NOTICE"));
/** The text of the card message. A closed constant: the card shows live state, so no status is frozen into the message. */
export const REQUEST_CARD_TEXT = "Your request";
// --- the durable intent keys -------------------------------------------------------------------------------------------------------------
//
// The key of a durable intent names WHAT to look at, never what to say. The card key has no version: one card per request, whenever
// and however often the SUBMITTED event is delivered. The notice key is the dedupe key of the outbox row itself
// (`request:<id>:<event>:<version>`), so the handler reads that row and the intent cannot drift from it.
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const CARD_KEY = new RegExp(`^request-card:(${UUID})$`);
const NOTICE_KEY = new RegExp(`^request:(${UUID}):([A-Z_]{1,40}):([1-9][0-9]{0,8})$`);
export const requestCardIntentKey = (requestId) => `request-card:${requestId}`;
export const requestNoticeIntentKey = (requestId, event, version) => requestEventDedupeKey(requestId, event, version);
/** The request, event and version of an intent key, or `null` when the key is not one of ours or names an event that posts nothing. */
export function parseRequestProjectionIntentKey(key) {
    const card = CARD_KEY.exec(key);
    if (card !== null)
        return { kind: "CARD", requestId: card[1] };
    const notice = NOTICE_KEY.exec(key);
    if (notice === null)
        return null;
    const event = notice[2];
    if (STREAM_PLAN_OF_EVENT[event] !== "NOTICE")
        return null;
    return {
        kind: "NOTICE",
        requestId: notice[1],
        event,
        version: Number(notice[3]),
    };
}
/** The Stream message id of an intent key. Deterministic, so a retry, a duplicate and a second host post one message. */
export function requestStreamMessageId(intentKey) {
    return systemNoticeMessageId(requestStreamNoticeId(intentKey));
}
export function requestStreamNoticeId(intentKey) {
    return noticeIdFromEvent(intentKey);
}
const partRefOf = (requestId) => Object.freeze({
    k: "concierge-request",
    v: 1,
    refs: Object.freeze([Object.freeze({ type: "CONCIERGE_REQUEST", id: requestId, label: null })]),
});
/** The message of the card: a closed text and an id reference. A DRAFT has no card. */
export function requestCardMessage(input) {
    const noticeId = requestStreamNoticeId(input.intentKey);
    return Object.freeze({
        id: systemNoticeMessageId(noticeId),
        text: REQUEST_CARD_TEXT,
        envelope: Object.freeze({
            v: 1,
            kind: "REQUEST_CARD",
            noticeId,
            requestId: input.requestId,
            parts: Object.freeze([partRefOf(input.requestId)]),
        }),
    });
}
/** The message of a notice: the closed status text of the CURRENT state, never the text of the event, a staff note or a model. */
export function requestNoticeMessage(input) {
    const noticeId = requestStreamNoticeId(input.intentKey);
    return Object.freeze({
        id: systemNoticeMessageId(noticeId),
        text: requestCustomerStatusText(input.state),
        envelope: Object.freeze({
            v: 1,
            kind: "REQUEST_NOTICE",
            noticeId,
            requestId: input.requestId,
            statusCode: requestStatusCodes[input.state],
            parts: Object.freeze([partRefOf(input.requestId)]),
        }),
    });
}
/** The keys of a card envelope, of a notice envelope and of a message part reference (the leak and the strict-schema tests pin them). */
export const REQUEST_CARD_ENVELOPE_KEYS = Object.freeze([
    "v",
    "kind",
    "noticeId",
    "requestId",
    "parts",
]);
export const REQUEST_NOTICE_ENVELOPE_KEYS = Object.freeze([
    "v",
    "kind",
    "noticeId",
    "requestId",
    "statusCode",
    "parts",
]);
/**
 * A notice is stale when a LATER meaningful event of the same request exists. That later event posts its own notice with the status text of
 * the current state, so this one would only repeat an older word. The facts are the versions of the customer outbox rows of the request.
 * A card is never stale (there is one per request).
 */
export function isRequestNoticeStale(input) {
    return input.customerNoticeVersions.some((version) => version > input.eventVersion);
}
/** The states whose customer updates travel with the next action (CONFIRMED: the details; UNAVAILABLE: the note and the alternatives). */
const STATES_WITH_UPDATES = Object.freeze([
    "CONFIRMED",
    "UNAVAILABLE",
]);
export const PROJECTED_UPDATES_MAX = 50;
/** The keys of every object a customer projection holds. The leak tests compare the real keys with these. */
export const CUSTOMER_REQUEST_PROJECTION_KEYS = Object.freeze([
    "requestId",
    "state",
    "statusCode",
    "statusText",
    "card",
    "action",
    "nextAction",
]);
export const CUSTOMER_NEXT_ACTION_KEYS = Object.freeze([
    "step",
    "question",
    "paymentRequestId",
    "updates",
]);
export const CUSTOMER_PROJECTED_UPDATE_KEYS = Object.freeze([
    "id",
    "kind",
    "text",
    "isCorrection",
    "createdAt",
]);
export const CUSTOMER_PROJECTED_QUESTION_KEYS = Object.freeze(["questionId", "prompt", "fields"]);
const pickUpdate = (update) => Object.freeze({
    id: update.id,
    kind: update.kind,
    text: update.text,
    isCorrection: update.isCorrection,
    createdAt: update.createdAt,
});
/**
 * The customer's view of one request. Pure. It names every field it copies, so an extra field of the record (a note, a reason, an owner, a
 * supplier) has nowhere to go. The status text is the closed text of the state.
 */
export function projectRequestForCustomer(facts, context) {
    const payment = facts.state === "PAYMENT_REQUIRED" ? context.payment : null;
    const action = projectRequestV2({
        id: facts.id,
        state: facts.state,
        title: facts.title,
        hasConversation: facts.hasConversation,
        tripId: facts.tripId,
        payment,
        updatedAt: facts.updatedAt,
    });
    const updates = STATES_WITH_UPDATES.includes(facts.state)
        ? context.updates.slice(-PROJECTED_UPDATES_MAX).map(pickUpdate)
        : [];
    const question = facts.state === "AWAITING_CUSTOMER" && context.openQuestion !== null
        ? Object.freeze({
            questionId: context.openQuestion.questionId,
            prompt: context.openQuestion.prompt,
            fields: context.openQuestion.fields,
        })
        : null;
    return Object.freeze({
        requestId: facts.id,
        state: facts.state,
        statusCode: requestStatusCodes[facts.state],
        statusText: requestCustomerStatusText(facts.state),
        card: facts.state === "DRAFT"
            ? null
            : Object.freeze({ part: "concierge-request", version: 1, requestId: facts.id }),
        action,
        nextAction: Object.freeze({
            step: action.nextStep,
            question,
            paymentRequestId: payment === null ? null : payment.paymentRequestId,
            updates: Object.freeze(updates),
        }),
    });
}
export const STAFF_REQUEST_PROJECTION_KEYS = Object.freeze([
    "requestId",
    "flags",
    "exception",
    "attention",
    "lastCustomerActivityAt",
    "customerRespondedAt",
    "updatedSinceViewed",
]);
/** A request that no staff member has started: SUBMITTED, or ASSIGNED and not yet claimed. */
const isNewRequest = (state) => state === "SUBMITTED" || state === "ASSIGNED";
/**
 * The staff attention of one request. Pure. It reads no chat state: a concierge sees a new, a changed or an answered request even
 * when the customer's conversation has no unread message (or no conversation at all).
 */
export function projectRequestForStaff(facts, context) {
    const flags = requestAttentionFlags(facts, context);
    const attention = [];
    if (isNewRequest(facts.state))
        attention.push("REQUEST_SUBMITTED");
    if (facts.latestEvent === "CUSTOMER_RESPONDED" && facts.state === "IN_PROGRESS") {
        attention.push("REQUEST_CUSTOMER_RESPONDED");
    }
    if (isUnassignedRequest(facts))
        attention.push("REQUEST_UNASSIGNED");
    if (flags.includes("AGING"))
        attention.push("REQUEST_AGING");
    const viewedAt = context.viewedAt ?? null;
    const newest = [facts.stateChangedAt, facts.lastCustomerActivityAt].reduce((latest, at) => (at !== null && at.getTime() > latest.getTime() ? at : latest), facts.stateChangedAt);
    return Object.freeze({
        requestId: facts.id,
        flags: Object.freeze(flags),
        exception: requestException(facts),
        attention: Object.freeze(attention),
        lastCustomerActivityAt: facts.lastCustomerActivityAt,
        customerRespondedAt: facts.customerRespondedAt,
        updatedSinceViewed: viewedAt === null ? null : newest.getTime() > viewedAt.getTime(),
    });
}
const step = (name, event, from, to, assignee, rest) => {
    const stream = STREAM_PLAN_OF_EVENT[event];
    const [withConversation, withoutConversation] = Array.isArray(rest.nextStep)
        ? rest.nextStep
        : [rest.nextStep, rest.nextStep];
    return Object.freeze({
        step: name,
        event,
        from,
        to,
        assignee,
        stream,
        noticeText: stream === "NOTICE" ? requestCustomerStatusText(to) : null,
        action: Object.freeze({
            bucket: rest.bucket,
            statusCode: requestStatusCodes[to],
            nextStep: Object.freeze({ withConversation, withoutConversation }),
            carriesQuestion: rest.carries?.question === true,
            carriesPayment: rest.carries?.payment === true,
            carriesUpdates: rest.carries?.updates === true,
        }),
        staff: Object.freeze({
            flags: Object.freeze(rest.flags ?? []),
            attention: Object.freeze(rest.attention ?? []),
            exception: rest.exception ?? null,
        }),
    });
};
const CONVERSATION_STEP = ["OPEN_CONVERSATION", "NONE"];
export const requestLifecycleExpectations = Object.freeze([
    step("submit", "SUBMITTED", "DRAFT", "SUBMITTED", "NONE", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
        flags: ["UNASSIGNED"],
        attention: ["REQUEST_SUBMITTED", "REQUEST_UNASSIGNED"],
        exception: "COVERAGE_GAP",
    }),
    step("assign to a named concierge", "ASSIGNED", "SUBMITTED", "ASSIGNED", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
        attention: ["REQUEST_SUBMITTED"],
    }),
    step("assign to the supervisor queue", "ASSIGNED", "SUBMITTED", "ASSIGNED", "QUEUE", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
        flags: ["UNASSIGNED"],
        attention: ["REQUEST_SUBMITTED", "REQUEST_UNASSIGNED"],
        exception: "COVERAGE_GAP",
    }),
    step("claim", "CLAIMED", "ASSIGNED", "IN_PROGRESS", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
    }),
    step("ask the customer", "AWAITING_CUSTOMER", "IN_PROGRESS", "AWAITING_CUSTOMER", "NAMED", {
        bucket: "NEEDS_YOU",
        nextStep: "ANSWER_QUESTION",
        carries: { question: true },
        flags: ["AWAITING_CUSTOMER"],
    }),
    step("the customer answers", "CUSTOMER_RESPONDED", "AWAITING_CUSTOMER", "IN_PROGRESS", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
        attention: ["REQUEST_CUSTOMER_RESPONDED"],
    }),
    step("staff record the answer", "RESPONSE_RECORDED", "AWAITING_CUSTOMER", "IN_PROGRESS", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
    }),
    step("require payment", "PAYMENT_REQUIRED", "IN_PROGRESS", "PAYMENT_REQUIRED", "NAMED", {
        bucket: "NEEDS_YOU",
        nextStep: "OPEN_PAYMENT",
        carries: { payment: true },
    }),
    step("resolve payment", "PAYMENT_RESOLVED", "PAYMENT_REQUIRED", "IN_PROGRESS", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
    }),
    // A staff replan after a payment that failed, expired or was cancelled hands the work back: the same state class as RESPONSE_RECORDED and
    // PAYMENT_RESOLVED (no outbox row, so no notice; the item returns to With concierge on the next read).
    step("replan payment", "PAYMENT_REPLANNED", "PAYMENT_REQUIRED", "IN_PROGRESS", "NAMED", {
        bucket: "WITH_CONCIERGE",
        nextStep: CONVERSATION_STEP,
    }),
    step("confirm", "CONFIRMED", "IN_PROGRESS", "CONFIRMED", "NAMED", {
        bucket: "CONFIRMED",
        nextStep: "VIEW_CONFIRMATION",
        carries: { updates: true },
    }),
    step("mark unavailable", "UNAVAILABLE", "IN_PROGRESS", "UNAVAILABLE", "NAMED", {
        bucket: null,
        nextStep: "NONE",
        carries: { updates: true },
    }),
    step("complete", "COMPLETED", "CONFIRMED", "COMPLETED", "NAMED", {
        bucket: null,
        nextStep: "NONE",
    }),
    step("cancel", "CANCELLED", "IN_PROGRESS", "CANCELLED", "NAMED", {
        bucket: null,
        nextStep: "NONE",
    }),
    step("decline", "DECLINED", "IN_PROGRESS", "DECLINED", "NAMED", {
        bucket: null,
        nextStep: "NONE",
    }),
    step("expire", "EXPIRED", "AWAITING_CUSTOMER", "EXPIRED", "NAMED", {
        bucket: null,
        nextStep: "NONE",
    }),
]);
/**
 * Events that change nothing the customer or the chat shows. Each is replayed too: it must post nothing and move no projection. A
 * correction of a confirmation is one of them (it adds a customer update, not a state), so the customer reads it on the live card.
 */
export const requestStaffOnlyEvents = Object.freeze([
    "CREATED",
    "DRAFT_UPDATED",
    "OWNER_REASSIGNED",
    "PRIORITY_CHANGED",
    "STATUS_SUMMARY_UPDATED",
    "WORK_STATUS_CHANGED",
]);
